/**
 * Centralized JSON Schema definitions for Mentor Gateway structured responses
 * Following OpenAI JSON Schema spec with strict mode (additionalProperties: false)
 * 
 * Based on docs/STRUCTURED_OUTPUT_SCHEMA.md
 */

/**
 * Schema for book recommendation item
 */
const BookRecommendationItemSchema = {
  type: 'object',
  properties: {
    bookId: {
      type: 'integer',
      description: 'Numeric book ID (e.g., 107).'
    },
    // reason: { type: 'string' }
  },
  required: ['bookId'],
  additionalProperties: false
} as const;

/**
 * Schema for insight recommendation item
 */
const InsightRecommendationItemSchema = {
  type: 'object',
  properties: {
    insightId: {
      type: 'integer',
      description: 'Numeric insight ID.'
    },
    // reason: { type: 'string' }
  },
  required: ['insightId'],
  additionalProperties: false
} as const;

/**
 * Schema for chat response with optional book and insight recommendations
 * Used in regular mentor chat when user asks for content recommendations
 */
export const ChatWithRecommendationsSchema = {
  type: 'object',
  properties: {
    text: { type: 'string' },
    books: {
      type: 'array',
      minItems: 0,
      items: BookRecommendationItemSchema
    },
    insights: {
      type: 'array',
      minItems: 0,
      items: InsightRecommendationItemSchema
    },
    // Optional profile updates suggested by the mentor based on the conversation
    profileUpdates: {
      anyOf: [
        {
          type: 'object',
          properties: {
            coreProfile: {
              anyOf: [
                {
                  type: 'object',
                  properties: {
                    goals: { type: 'array', items: { type: 'string' } },
                    interests: { type: 'array', items: { type: 'string' } },
                    challenges: { type: 'array', items: { type: 'string' } },
                    values: { type: 'array', items: { type: 'string' } },
                    personalityTraits: { type: 'array', items: { type: 'string' } },
                  },
                  required: ['goals', 'interests', 'challenges', 'values', 'personalityTraits'],
                  additionalProperties: false
                },
                { type: 'null' }
              ]
            },
            activeContext: {
              anyOf: [
                {
                  type: 'object',
                  properties: {
                    currentFocus: { type: 'array', items: { type: 'string' } },
                    urgentNeeds: { type: 'array', items: { type: 'string' } },
                    emotionalState: { type: 'string' },
                    information: { type: 'string' },
                  },
                  required: ['currentFocus', 'urgentNeeds', 'emotionalState', 'information'],
                  additionalProperties: false
                },
                { type: 'null' }
              ]
            },
            changesSummary: { anyOf: [ { type: 'string' }, { type: 'null' } ] },
          },
          required: ['coreProfile', 'activeContext', 'changesSummary'],
          additionalProperties: false
        },
        { type: 'null' }
      ]
    },
    // Focus change detection for conversational updates
    focusChange: {
      anyOf: [
        {
          type: 'object',
          properties: {
            detected: { type: 'boolean' },
            newCategory: {
              type: 'string',
              enum: [
                'health_wellness',
                'career_professional',
                'leadership_management',
                'entrepreneurship_business',
                'relationships_family',
                'personal_development',
                'finance_wealth',
                'learning_skills',
                'productivity_time',
                'purpose_meaning'
              ]
            },
            newLabel: { type: 'string' },
            reason: { type: 'string' },
          },
          required: ['detected', 'newCategory', 'newLabel', 'reason'],
          additionalProperties: false
        },
        { type: 'null' }
      ]
    }
  },
  // Strict mode: required must include every key in properties we care about
  // We require books/insights arrays even if empty
  required: ['text', 'books', 'insights', 'profileUpdates', 'focusChange'],
  additionalProperties: false
} as const;

/**
 * Schema for onboarding profile generation
 * Used in CalculationScreen to generate profile summary, books, and insights
 */
export const OnboardingProfileSchema = {
  type: 'object',
  properties: {
    mainFocusCategory: {
      type: 'string',
      enum: [
        'health_wellness',
        'career_professional',
        'leadership_management',
        'entrepreneurship_business',
        'relationships_family',
        'personal_development',
        'finance_wealth',
        'learning_skills',
        'productivity_time',
        'purpose_meaning'
      ]
    },
    mainFocusLabel: { type: 'string' },
    personalizedMessage: { type: 'string' },
    profileSummary: { type: 'string' },
    recommendedBooks: {
      type: 'array',
      minItems: 6,
      maxItems: 6,
      items: BookRecommendationItemSchema
    },
    insightPlan: {
      type: 'object',
      properties: {
        title: { type: 'string' },
        insights: {
          type: 'array',
          minItems: 3,
          maxItems: 5,
          items: InsightRecommendationItemSchema
        }
      },
      required: ['title', 'insights'],
      additionalProperties: false
    }
  },
  required: ['mainFocusCategory', 'mainFocusLabel', 'personalizedMessage', 'profileSummary', 'recommendedBooks', 'insightPlan'],
  additionalProperties: false
} as const;

/**
 * Schema for goal validation during onboarding
 * Single AI call that acknowledges, validates, and routes
 */
export const GoalValidationSchema = {
  type: 'object',
  properties: {
    acknowledgment: { type: 'string' }, // Friendly acknowledgment of the user's goal
    isGoalStrong: { type: 'boolean' },
    analysis: { type: 'string' },
    suggestedPath: { 
      type: 'string',
      enum: ['continue_with_goal', 'explore_with_guidance', 'needs_clarification']
    },
    feedback: { type: 'string' }, // Additional feedback only if goal needs clarification
    extractedGoals: {
      type: 'array',
      items: { type: 'string' },
      minItems: 0
    },
    extractedChallenges: {
      type: 'array',
      items: { type: 'string' },
      minItems: 0
    }
  },
  required: ['acknowledgment', 'isGoalStrong', 'analysis', 'suggestedPath', 'feedback', 'extractedGoals', 'extractedChallenges'],
  additionalProperties: false
} as const;

// ====================================
// TWO-PHASE REQUEST SCHEMAS
// ====================================

/**
 * Phase 1 Schema - Fast Response (Onboarding)
 * Analyze user's onboarding answers and extract profile + search query
 * Used to show immediate feedback and build user profile
 */
export const OnboardingPhase1Schema = {
  type: 'object',
  properties: {
    mainFocusCategory: {
      type: 'string',
      enum: [
        'health_wellness',
        'career_professional',
        'leadership_management',
        'entrepreneurship_business',
        'relationships_family',
        'personal_development',
        'finance_wealth',
        'learning_skills',
        'productivity_time',
        'purpose_meaning'
      ]
    },
    mainFocusLabel: { 
      type: 'string',
      description: 'Human-readable main focus (e.g., "Health & Wellness", "Career Growth")'
    },
    text: { 
      type: 'string',
      description: 'Short welcoming message (1-2 sentences)'
    },
    searchQuery: { 
      type: 'string',
      description: 'Simple search query for phase 2 (2-4 key terms for vector search, e.g., "focus founders" or "parenting sleep")'
    },
    coreProfile: {
      type: 'object',
      properties: {
        goals: {
          type: 'array',
          items: { type: 'string' },
          description: 'User goals extracted from onboarding answers'
        },
        interests: {
          type: 'array',
          items: { type: 'string' },
          description: 'User interests from onboarding'
        },
        challenges: {
          type: 'array',
          items: { type: 'string' },
          description: 'Challenges the user faces'
        },
        personalityTraits: {
          type: 'array',
          items: { type: 'string' },
          description: 'Key personality traits observed'
        }
      },
      required: ['goals', 'interests', 'challenges', 'personalityTraits'],
      additionalProperties: false,
      description: 'Extracted profile data from onboarding answers'
    }
  },
  required: ['mainFocusCategory', 'mainFocusLabel', 'text', 'searchQuery', 'coreProfile'],
  additionalProperties: false
} as const;

/**
 * Phase 2 Schema - Recommendations (Onboarding)
 * Provide searchQuery from phase 1 to get books and insights
 * Skips intent extraction for faster results
 */
export const OnboardingPhase2Schema = {
  type: 'object',
  properties: {
    personalizedMessage: { 
      type: 'string',
      description: 'Personalized message introducing the recommendations and summarizing user profile (2-3 sentences)'
    },
    recommendedBooks: {
      type: 'array',
      minItems: 6,
      maxItems: 6,
      items: BookRecommendationItemSchema
    },
    insightPlan: {
      type: 'object',
      properties: {
        title: { type: 'string' },
        insights: {
          type: 'array',
          minItems: 3,
          maxItems: 5,
          items: InsightRecommendationItemSchema
        }
      },
      required: ['title', 'insights'],
      additionalProperties: false
    }
  },
  required: ['personalizedMessage', 'recommendedBooks', 'insightPlan'],
  additionalProperties: false
} as const;

/**
 * Phase 1 Schema - Fast Response (Chat)
 * Returns text response + search query for recommendations
 * AI decides if recommendations are needed by returning empty string for searchQuery
 */
export const ChatPhase1Schema = {
  type: 'object',
  properties: {
    text: { 
      type: 'string',
      description: 'If searchQuery is not empty: 5-10 words max like "Let me find resources." If searchQuery is empty: normal 1-2 sentence response.'
    },
    searchQuery: { 
      type: 'string',
      description: 'Empty string "" for casual greetings. For recommendations: 2-4 key terms only (e.g., "focus", "sales founders", "parenting sleep")'
    },
    profileChanges: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          operation: {
            type: 'string',
            enum: ['add', 'remove', 'set'],
            description: 'add/remove for array fields (goals, interests, etc.), set for single-value fields (mainFocusCategory, mainFocusLabel)'
          },
          field: {
            type: 'string',
            enum: ['goals', 'interests', 'challenges', 'values', 'personalityTraits', 'mainFocusLabel'],
            description: 'Which profile field to modify'
          },
          value: {
            type: 'string',
            description: 'The value to add/remove/set. For mainFocusLabel use human-readable text like "Health & Wellness"'
          }
        },
        required: ['operation', 'field', 'value'],
        additionalProperties: false
      },
      description: 'Profile changes as compact operations. Empty array [] if no changes. Examples: [{"operation":"add","field":"goals","value":"Learn public speaking"}] or [{"operation":"set","field":"mainFocusLabel","value":"Health & Wellness"}]'
    }
  },
  required: ['text', 'searchQuery', 'profileChanges'], // All fields required per OpenAI strict mode
  additionalProperties: false
} as const;

/**
 * Phase 2 Schema - Recommendations (Chat)
 * Provide searchQuery from phase 1 to get books and insights
 * This appears as a separate message in the chat
 */
export const ChatPhase2Schema = {
  type: 'object',
  properties: {
    text: { 
      type: 'string',
      description: 'One sentence that mentions something specific about the user (their goal, challenge, or interest) and why these recommendations will help them.'
    },
    books: {
      type: 'array',
      minItems: 0,
      maxItems: 4,
      items: BookRecommendationItemSchema
    },
    insights: {
      type: 'array',
      minItems: 0,
      maxItems: 3,
      items: InsightRecommendationItemSchema
    }
  },
  required: ['text', 'books', 'insights'],
  additionalProperties: false
} as const;

/**
 * Schema for on-demand book personalization
 * Used to generate hyper-personalized messages for individual books
 * when existing recommendations don't mention user's name or profile
 */
export const BookPersonalizationSchema = {
  type: 'object',
  properties: {
    personalizedMessage: { 
      type: 'string',
      description: 'A natural, conversational 1-2 sentence message connecting this book to the user\'s specific situation. Mention their name naturally (not always at the start), reference their actual goals or challenges, and vary your phrasing. Avoid formulaic patterns like "this book is perfect for you because". Instead, speak directly about how the book addresses their specific needs.'
    }
  },
  required: ['personalizedMessage'],
  additionalProperties: false
} as const;

/**
 * Schema for batching book personalization (multiple books in one call)
 */
export const BookPersonalizationBatchSchema = {
  type: 'object',
  properties: {
    personalizations: {
      type: 'array',
      minItems: 1,
      items: {
        type: 'object',
        properties: {
          bookId: {
            type: 'string',
            description: 'The exact bookId provided in the request (keep suffixes like _en if present)'
          },
          personalizedMessage: {
            type: 'string',
            description: '1-2 sentence message tying the book to the user with varied openings and tone. Mention name naturally but not always first.'
          }
        },
        required: ['bookId', 'personalizedMessage'],
        additionalProperties: false
      },
      description: 'Personalized messages for each requested book, maintaining the same order and IDs.'
    }
  },
  required: ['personalizations'],
  additionalProperties: false
} as const;

